/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';

/**
 * Global overview cards (096) at the top of the Subscriptions list, on the shared au-card
 * system: Total Active Subscriptions (split by plan, by platform and by auto-renew) first,
 * since the live base is what most billing questions start from, then Total Expired and
 * Manual Users. Active is the wide card on the left; the two small ones stack in a column
 * beside it, so neither is stretched to Active's height with nothing to put in the space.
 *
 * Active counts PAID subscriptions only — an admin free-access grant is live but nobody is
 * paying for it, so it is counted once, on the Manual Users card. Active + Expired therefore
 * does NOT equal the whole base; active grants sit outside both.
 *
 * These three figures cover the WHOLE subscription base and deliberately ignore every filter
 * on the page — date range, status, platform, renew, search, the watch segment, the lot. They
 * answer "where does the business stand", while the table below answers "who matches this
 * filter". Nothing here should ever be wired to a filter.
 *
 * That caveat, and what each card counts, live behind each card's `i` button rather than in
 * standing text on the page: they are what an operator needs the first time and clutter every
 * time after. Every card explains itself the same way, so the row reads as one thing and
 * nothing on the face of a card is prose.
 *
 * Every value is written out as text with tabular numerals — no colour-only meaning.
 */
const n = (v) => Number(v || 0).toLocaleString();

const pct = (count, total) => {
  const t = Number(total) || 0;
  if (t <= 0) return '0%';
  const r = Math.round((Number(count || 0) / t) * 100);
  return r < 1 && Number(count) > 0 ? '<1%' : `${r}%`;
};

/**
 * One split of the Active card: a caption, a proportion bar, and one line per bucket
 * carrying its swatch, name, count and share of Active. Three of these sit side by side,
 * so plan, platform and auto-renew read as three small tables rather than three runs of
 * chips — the figures line up in a column and can be compared down the card.
 *
 * `of` is the Active total, so each share is read against Active rather than against every
 * subscription. The bar only reinforces numbers that are already written out, so colour is
 * never the sole carrier; it also carries the whole split in one aria-label for a reader
 * that would otherwise get a bare graphic. A bucket flagged `hideWhenEmpty` (the catch-all
 * "Other") disappears at zero, so the common case reads as a clean two-way split.
 *
 * An item may also carry `sub` — a second, quieter line breaking that bucket down further
 * (auto-renew On / Off by plan). It is dropped entirely when the bucket itself is empty,
 * since "Monthly 0 · Yearly 0" under a zero says nothing.
 */
const Split = ({ testId, caption, items = [], of }) => {
  const shown = items.filter((i) => !i.hideWhenEmpty || Number(i.count) > 0);
  const total = Number(of) || 0;
  const share = (c) => (total > 0 ? (Number(c || 0) / total) * 100 : 0);
  const label = shown.map((i) => `${i.label} ${n(i.count)} of ${n(total)} (${pct(i.count, total)})`).join(', ');
  return (
    <div className="sub-ov__split" data-testid={testId}>
      <span className="sub-ov__caption">{caption}</span>
      <span className="sub-ov__bar" role="img" aria-label={label}>
        {shown.map((i, idx) => (
          <span key={i.label} className={`sub-ov__seg sub-ov__seg--${idx + 1}`} style={{ width: `${share(i.count)}%` }} />
        ))}
      </span>
      <ul className="sub-ov__rows">
        {shown.map((i, idx) => (
          <li className="sub-ov__item" key={i.label}>
            <span className="sub-ov__row">
              <span className={`sub-ov__dot sub-ov__dot--${idx + 1}`} aria-hidden="true" />
              <span className="sub-ov__k">{i.label}</span>
              <strong className="sub-ov__v tnum">{n(i.count)}</strong>
              <span className="sub-ov__p tnum">{pct(i.count, total)}</span>
            </span>
            {i.sub && Number(i.count) > 0 && (
              <span className="sub-ov__sub" data-testid={i.subTestId}>
                {i.sub.filter((b) => !b.hideWhenEmpty || Number(b.count) > 0).map((b) => (
                  <span className="sub-ov__subitem" key={b.label}>
                    {b.label} <strong className="tnum">{n(b.count)}</strong>
                  </span>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Monthly / Yearly under one auto-renew bucket; Other only when that bucket has any.
const planSub = (plans = {}) => [
  { label: 'Monthly', count: plans.monthly },
  { label: 'Yearly', count: plans.yearly },
  { label: 'Other', count: plans.other, hideWhenEmpty: true },
];

/**
 * A card, with its "what does this count" note behind an `i` button.
 *
 * A button rather than a `title` tooltip, because a hover tooltip is unreachable on a phone
 * and unreliable for a keyboard. Closes on a second click, on Escape and on a click outside —
 * the three ways anyone tries to dismiss a note. Pressing another card's button is an outside
 * click for this one, so only one note is ever open without any shared state.
 *
 * The note floats: it is positioned out of flow, so opening it never changes the card's width
 * or height and the row never reflows under the operator. It hangs off the card's TITLE ROW —
 * which spans the card's content width — rather than off the button itself: button-anchored it
 * ran off the right edge of the page on the last card and scrolled the whole screen sideways,
 * while the title row gives it the card's own left and right edges to line up with.
 *
 * Each note repeats the "these cards ignore the filters" line. That is not duplication to be
 * factored out: a note is read on its own, and whoever opens this one never sees the others,
 * so each has to be complete.
 */
const OverviewCard = ({ id, title, note, children }) => {
  const [open, setOpen] = useState(false);
  const card = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (card.current && !card.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="au-card" data-testid={`sub-ov-${id}`} ref={card}>
      <span className="au-card__label">
        {title}
        <button
          type="button"
          className="sub-ov__info-btn"
          data-testid={`sub-ov-info-btn-${id}`}
          aria-expanded={open}
          aria-label={`What ${title} counts`}
          onClick={() => setOpen((v) => !v)}
        >
          i
        </button>
        {open && (
          <span className="sub-ov__info" role="note" data-testid={`sub-ov-info-${id}`}>
            <span className="sub-ov__info-p">{note}</span>
            <span className="sub-ov__info-p">
              These three cards count the whole subscription base and <b>ignore every filter</b> on
              this page, the watch segment included.
            </span>
          </span>
        )}
      </span>
      {children}
    </div>
  );
};

const SubscriptionOverviewCards = ({ overview }) => {
  if (overview && overview.error) {
    return (
      <div className="au-cards sub-ov" data-testid="sub-overview">
        <div className="au-card sub-ov__error" data-testid="sub-ov-error">
          <span className="au-card__label">Subscription totals</span>
          <span className="au-card__sub">Couldn’t load the totals. Reload the page to try again.</span>
        </div>
      </div>
    );
  }

  const o = overview || {};
  const active = o.active || {};
  const plan = active.plan || {};
  const platform = active.platform || {};
  const renew = active.auto_renew || {};
  const byPlan = renew.by_plan || {};
  const manual = o.manual || {};
  const activeTotal = Number(active.total) || 0;

  return (
    <div className="au-cards sub-ov" data-testid="sub-overview">
      <OverviewCard
        id="active" title="Total Active Subscriptions"
        note={<><b>Paid subscriptions only</b> — admin grants are counted in Manual Users.</>}
      >
        <span className="au-card__value tnum">{n(activeTotal)}</span>
        <div className="sub-ov__splits">
          <Split
            testId="sub-ov-plan" caption="Plan" of={activeTotal}
            items={[
              { label: 'Monthly', count: plan.monthly },
              { label: 'Yearly', count: plan.yearly },
              { label: 'Other', count: plan.other, hideWhenEmpty: true },
            ]}
          />
          <Split
            testId="sub-ov-platform" caption="Platform" of={activeTotal}
            items={[
              { label: 'Android', count: platform.android },
              { label: 'iOS', count: platform.ios },
              { label: 'Other', count: platform.other, hideWhenEmpty: true },
            ]}
          />
          <Split
            testId="sub-ov-renew" caption="Auto-renew" of={activeTotal}
            items={[
              { label: 'On', count: renew.on, subTestId: 'sub-ov-renew-on-plans', sub: planSub(byPlan.on) },
              { label: 'Off', count: renew.off, subTestId: 'sub-ov-renew-off-plans', sub: planSub(byPlan.off) },
            ]}
          />
          </div>
        </OverviewCard>

        <div className="sub-ov__side" data-testid="sub-ov-side">
          <OverviewCard
            id="expired" title="Total Expired"
            note={<>Subscriptions <b>no longer active</b> — every store, every date, admin grants
              that have run out included.</>}
          >
            <span className="au-card__value tnum">{n(o.expired)}</span>
            {Number(o.total) > 0 && (
              <span className="au-card__sub">{pct(o.expired, o.total)} of {n(o.total)} subscriptions</span>
            )}
          </OverviewCard>

          <OverviewCard
            id="manual" title="Manual Users"
            note={<>Free access <b>granted by an admin</b>, plus the internal test accounts. Nobody
              paid a store for these, so they are never counted in Total Active Subscriptions.</>}
          >
            <span className="au-card__value tnum">{n(manual.total)}</span>
            <span className="au-card__sub">{n(manual.active)} still active</span>
          </OverviewCard>
      </div>
    </div>
  );
};

export default SubscriptionOverviewCards;
